import {isLeft} from 'fp-ts/lib/Either';
import {Type} from 'io-ts';
import {createErrorTable, getErrorPaths} from './debuggers';

export class DecodingError extends Error {
    readonly name = 'DecodingError';
    private constructor(message: string, options?: ErrorOptions) {
        super(message, options);
    }
    static from(error: Error) {
        error.name = this.prototype.name;
        Object.setPrototypeOf(error, this.prototype);
        return error;
    }
}

type NotPromise<I> = I extends Promise<any> ? 'YES, YOU DID FORGET `await`' : I;

export const mustBe =
    <A, O = A>(codec: Type<A, O, unknown>) =>
    <I>(value: NotPromise<I>): A =>
        decodeOrThrow(codec, value);

export const decodeOrThrow = <A, O = A, I = unknown>(codec: Type<A, O, I>, value: I): A => {
    const decoded = codec.decode(value);
    if (isLeft(decoded)) {
        if (import.meta.env.DEV) {
            console.table(createErrorTable(decoded.left));
            throw DecodingError.from(new Error('Data validation failed. See table above'));
        } else {
            throw DecodingError.from(new Error(getErrorPaths(decoded.left)));
        }
    }
    return decoded.right;
};

export const decodeOrFallback = <A, O = A, I = unknown>(codec: Type<A, O, I>, value: I, fallback: A): A => {
    const decoded = codec.decode(value);
    if (isLeft(decoded)) {
        return fallback;
    }
    return decoded.right;
};
